import * as React from 'react';

import { VerticalOverflowScrollArea } from '@/components/ui/vertical-overflow-scroll-area';
import { cn } from '@/lib/cn';

import { DesktopSidebarHoverProvider, useDesktopSidebarHover } from './DesktopSidebarHover';
import { SecondarySidebarSlotProvider } from './SecondarySidebarSlot';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { TopbarPortalProvider } from './TopbarPortal';

const PRIMARY_SIDEBAR_COLLAPSED_WIDTH = 56;
const PRIMARY_SIDEBAR_EXPANDED_WIDTH = 208;
const SECONDARY_SIDEBAR_COLLAPSED_WIDTH = 32;
const SECONDARY_SIDEBAR_EXPANDED_WIDTH = 200;
type DesktopNavZone = 'primary' | 'secondary' | 'content';

function normalizeDesktopNavZone(value: string | undefined): DesktopNavZone | null {
  if (value === 'primary' || value === 'secondary' || value === 'content') {
    return value;
  }
  return null;
}

function getHoveredDesktopNavZone(shell: HTMLDivElement): DesktopNavZone | null {
  const primaryZone = shell.querySelector<HTMLElement>('[data-desktop-nav-zone="primary"]');
  if (primaryZone?.matches(':hover')) {
    return 'primary';
  }

  const secondaryZone = shell.querySelector<HTMLElement>('[data-desktop-nav-zone="secondary"]');
  if (secondaryZone?.matches(':hover')) {
    return 'secondary';
  }

  const contentZone = shell.querySelector<HTMLElement>('[data-desktop-nav-zone="content"]');
  if (contentZone?.matches(':hover')) {
    return 'content';
  }

  return null;
}

function getDesktopNavZoneFromPointer(params: {
  fallbackZone: string | undefined;
  pointerX: number;
  shellWidth: number;
  primaryExpanded: boolean;
  secondaryExpanded: boolean;
  secondaryMounted: boolean;
}): DesktopNavZone | null {
  const {
    fallbackZone,
    pointerX,
    shellWidth,
    primaryExpanded,
    secondaryExpanded,
    secondaryMounted,
  } = params;
  const normalizedFallbackZone = normalizeDesktopNavZone(fallbackZone);

  if (shellWidth <= 0) {
    return normalizedFallbackZone;
  }

  if (pointerX < 0 || pointerX > shellWidth) {
    return null;
  }

  const primaryWidth = primaryExpanded
    ? PRIMARY_SIDEBAR_EXPANDED_WIDTH
    : PRIMARY_SIDEBAR_COLLAPSED_WIDTH;
  const secondaryWidth = secondaryMounted
    ? secondaryExpanded
      ? SECONDARY_SIDEBAR_EXPANDED_WIDTH
      : SECONDARY_SIDEBAR_COLLAPSED_WIDTH
    : 0;

  if (pointerX <= primaryWidth) {
    return 'primary';
  }

  if (secondaryMounted && pointerX <= primaryWidth + secondaryWidth) {
    return 'secondary';
  }

  return 'content';
}

export interface AppShellProps {
  /** Page content to render in the main area */
  children: React.ReactNode;
  /** Additional class names for the main content area */
  className?: string;
  /** Scroll mode for the main area (page = AppShell scrolls, content = page manages its own scroll) */
  scrollMode?: 'page' | 'content';
}

/**
 * Application shell layout combining Sidebar, Topbar, and main content area.
 *
 * Features:
 * - Responsive layout:
 *   - Desktop: fixed sidebar + main content
 *   - Mobile: collapsed sidebar with toggle
 * - Uses h-dvh (NOT h-screen) per ui-skills
 * - Fixed z-index scale for overlays
 * - Safe-area-inset support for mobile devices
 *
 * @example
 * <AppShell>
 *   <InvoicesPage />
 * </AppShell>
 */
function AppShellLayout({ children, className, scrollMode }: AppShellProps) {
  const desktopNav = useDesktopSidebarHover();
  const shellNodeRef = React.useRef<HTMLDivElement | null>(null);
  const hoverSyncFrameRef = React.useRef<number | null>(null);

  if (!desktopNav) {
    throw new Error('AppShellLayout must be used inside DesktopSidebarHoverProvider');
  }

  const {
    clearDesktopNavigation,
    primaryExpanded,
    secondaryExpanded,
    secondaryMounted,
    syncActiveZone,
  } = desktopNav;

  const syncActiveZoneFromDomHover = React.useCallback(() => {
    const shell = shellNodeRef.current;
    if (!shell || !shell.matches(':hover')) {
      return;
    }

    syncActiveZone(getHoveredDesktopNavZone(shell));
  }, [syncActiveZone]);

  const cancelPendingHoverSync = React.useCallback(() => {
    if (hoverSyncFrameRef.current === null) {
      return;
    }
    window.cancelAnimationFrame(hoverSyncFrameRef.current);
    hoverSyncFrameRef.current = null;
  }, []);

  const scheduleHoverSyncFromDom = React.useCallback(() => {
    cancelPendingHoverSync();
    syncActiveZoneFromDomHover();
    hoverSyncFrameRef.current = window.requestAnimationFrame(() => {
      hoverSyncFrameRef.current = null;
      syncActiveZoneFromDomHover();
    });
  }, [cancelPendingHoverSync, syncActiveZoneFromDomHover]);

  const handlePointerMoveCapture = React.useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch' || !shellNodeRef.current) {
        return;
      }

      const shell = shellNodeRef.current;
      const shellRect = shell.getBoundingClientRect();
      const target = event.target instanceof HTMLElement ? event.target : null;
      const fallbackZone = target?.closest<HTMLElement>('[data-desktop-nav-zone]')?.dataset.desktopNavZone;
      const pointerX = event.clientX - shellRect.left;

      syncActiveZone(
        getDesktopNavZoneFromPointer({
          fallbackZone,
          pointerX,
          shellWidth: shellRect.width,
          primaryExpanded,
          secondaryExpanded,
          secondaryMounted,
        }),
      );
    },
    [primaryExpanded, secondaryExpanded, secondaryMounted, syncActiveZone],
  );

  const handleShellRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (shellNodeRef.current) {
        shellNodeRef.current.removeEventListener('pointermove', handlePointerMoveCapture, true);
        shellNodeRef.current.removeEventListener('mouseleave', clearDesktopNavigation);
      }
      cancelPendingHoverSync();

      shellNodeRef.current = node;

      if (node) {
        node.addEventListener('pointermove', handlePointerMoveCapture, true);
        node.addEventListener('mouseleave', clearDesktopNavigation);
        scheduleHoverSyncFromDom();
      }
    },
    [cancelPendingHoverSync, clearDesktopNavigation, handlePointerMoveCapture, scheduleHoverSyncFromDom],
  );

  // Desktop sidebar stays as a compact rail and expands on hover.
  const desktopSidebarCollapsed = true;

  // Mobile sidebar open state (overlay mode)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Keep mobile overlay state aligned with the current viewport and keyboard dismissal.
  React.useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      if (!isMobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  return (
    <div
      ref={handleShellRef}
      className={cn(
        // Full viewport height using dvh (ui-skills: NEVER use h-screen)
        'flex h-dvh',
        // Background
        'bg-background',
      )}
    >
      {/* Desktop Sidebar - always visible on md+ (768px) */}
      <div className="hidden md:flex">
        <Sidebar collapsed={desktopSidebarCollapsed} hoverExpand />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen ? (
        <>
          {/* Backdrop */}
          <div
            className={cn(
              'fixed inset-0 bg-foreground/20 md:hidden',
              // Fixed z-index scale (ui-skills)
              'z-overlay',
            )}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Sidebar */}
          <div
            className={cn(
              'fixed inset-y-0 left-0 md:hidden',
              // Fixed z-index scale (ui-skills) - above overlay
              'z-overlay',
            )}
          >
            <Sidebar collapsed={false} onCollapsedChange={() => setMobileMenuOpen(false)} />
          </div>
        </>
      ) : null}

      {/* Secondary sidebar portal slot (renders between primary sidebar and content) */}
      <SecondarySidebarSlotProvider>
        {/* Main content area */}
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          data-desktop-nav-zone="content"
          data-testid="app-shell-content"
        >
          <TopbarPortalProvider>
            {/* Topbar */}
            <Topbar onMenuToggle={() => setMobileMenuOpen(true)} showMenuButton={true} />

            {/* Main content */}
            {scrollMode === 'page' ? (
              <VerticalOverflowScrollArea className="min-h-0 flex-1 overflow-auto" contentClassName="pr-0">
                <main
                  className={cn(
                    // Padding with safe-area-inset for mobile (ui-skills)
                    'p-4 lg:p-6',
                    'supports-[padding:env(safe-area-inset-bottom)]:pb-[max(1rem,env(safe-area-inset-bottom))]',
                    className,
                  )}
                >
                  {children}
                </main>
              </VerticalOverflowScrollArea>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <main
                  className={cn(
                    // Padding with safe-area-inset for mobile (ui-skills)
                    'flex min-h-0 flex-1 flex-col p-4 lg:p-6',
                    'supports-[padding:env(safe-area-inset-bottom)]:pb-[max(1rem,env(safe-area-inset-bottom))]',
                    className,
                  )}
                >
                  {children}
                </main>
              </div>
            )}
          </TopbarPortalProvider>
        </div>
      </SecondarySidebarSlotProvider>
    </div>
  );
}

export function AppShell({ children, className, scrollMode = 'page' }: AppShellProps) {
  return (
    <DesktopSidebarHoverProvider>
      <AppShellLayout {...(className ? { className } : {})} scrollMode={scrollMode}>
        {children}
      </AppShellLayout>
    </DesktopSidebarHoverProvider>
  );
}
